/**
 * Secure WebSocket Hook - Complete Rewrite with Singleflight and Connection Guards
 *
 * Features:
 * - Single connection guard (socketRef + connectingRef)
 * - Stable token acquisition with singleflight pattern
 * - Proper useEffect dependencies [isAuthenticated] only
 * - Connection management with backoff + jitter
 * - Token rotation before expiry
 * - Heartbeat and stall detection
 * - Comprehensive cleanup
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { useServerAuth } from './useAuth-cookie';
import { timelineKeys } from '@/types/execution-events';
import { API_BASE_URL } from '@/lib/api-base';
import { TokenManager, calculateReconnectDelay } from '@/lib/token-manager';

export interface UseSecureWebSocketOptions {
  executionId: string;
  onConnectionChange?: (status: ConnectionStatus) => void;
  onAuthFailure?: (error: string) => void;
  enablePollingFallback?: boolean;
  onEvent?: (eventType: string, payload: any) => void;
  onReconnectScheduled?: (delayMs: number, attempt: number) => void;
  // Demo-only safe overrides
  getToken?: () => Promise<{ token: string; expiresAt: number }>;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'auth_failed' | 'fallback';

export interface WebSocketState {
  status: ConnectionStatus;
  isConnected: boolean;
  isPollingFallback: boolean;
  error: string | null;
  lastUpdate: Date | null;
  // Connection control methods
  reconnect: () => void;
  disconnect: () => void;
  // Demo-only safe methods
  forceReauth?: () => Promise<{ token: string; expiresAt: number }>;
}

interface ConnectionRefs {
  socket: Socket | null;
  connecting: boolean;
  reconnectTimer: NodeJS.Timeout | null;
  heartbeatTimer: NodeJS.Timeout | null;
  refreshTimer: NodeJS.Timeout | null;
  reconnectAttempts: number;
  currentExecutionRoom: string | null;
  tokenManager: TokenManager;
}

export function useSecureWebSocketV2(options: UseSecureWebSocketOptions) {
  const { user, isAuthenticated } = useServerAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState({
    status: 'disconnected' as ConnectionStatus,
    isConnected: false,
    isPollingFallback: false,
    error: null as string | null,
    lastUpdate: null as Date | null
  });

  // Connection refs - these persist across renders
  const refs = useRef<ConnectionRefs>({
    socket: null,
    connecting: false,
    reconnectTimer: null,
    heartbeatTimer: null,
    refreshTimer: null,
    reconnectAttempts: 0,
    currentExecutionRoom: null,
    tokenManager: new TokenManager({
      getTokenOverride: options.getToken
    }),
  });

  // Update token manager when override changes
  useEffect(() => {
    refs.current.tokenManager.setTokenOverride(options.getToken);
  }, [options.getToken]);

  /**
   * Update connection status
   */
  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    setState(prev => {
      if (prev.status !== newStatus) {
        console.log(`🔌 WebSocket status: ${prev.status} → ${newStatus}`);
        options.onConnectionChange?.(newStatus);
        return {
          ...prev,
          status: newStatus,
          isConnected: newStatus === 'connected',
          isPollingFallback: newStatus === 'fallback',
        };
      }
      return prev;
    });
  }, [options.onConnectionChange]);

  /**
   * Handle data updates and invalidate cache
   */
  const handleDataUpdate = useCallback((data: any, source: 'websocket' | 'polling') => {
    setState(prev => ({
      ...prev,
      lastUpdate: new Date(),
      error: null
    }));

    // Timeline events invalidation
    if (data.events && Array.isArray(data.events)) {
      queryClient.invalidateQueries({
        queryKey: timelineKeys.execution(options.executionId)
      });
      queryClient.invalidateQueries({
        queryKey: ['timeline', options.executionId, 'filtered']
      });
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId, 'events']
      });
    }

    // Execution-level invalidations
    if (data.execution) {
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId, 'status']
      });
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId, 'overview']
      });
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId, 'metrics']
      });
    }

    // Stage-level invalidations
    if (data.stage) {
      queryClient.invalidateQueries({
        queryKey: ['execution', options.executionId, 'stages']
      });
      if (data.stage.stage_name) {
        queryClient.invalidateQueries({
          queryKey: ['execution', options.executionId, 'stages', data.stage.stage_name]
        });
      }
    }

    console.log(`📡 Cache invalidated by ${source} update for execution ${options.executionId}`);
  }, [options.executionId, queryClient]);

  /**
   * Handle execution room join/leave
   */
  const joinExecutionRoom = useCallback((executionId: string) => {
    if (!refs.current.socket?.connected || !executionId) return;

    // Leave previous execution room if any
    if (refs.current.currentExecutionRoom) {
      refs.current.socket.emit('leave', { type: 'execution', id: refs.current.currentExecutionRoom });
      console.log(`🚪 Left execution room: ${refs.current.currentExecutionRoom}`);
    }

    // Join new execution room
    refs.current.socket.emit('join', { type: 'execution', id: executionId });
    refs.current.currentExecutionRoom = executionId;
    console.log(`🚪 Joined execution room: ${executionId}`);
  }, []);

  /**
   * Socket.IO handles heartbeat automatically with pingInterval=25s and pingTimeout=5-20s
   * We don't need custom ping/pong - just rely on built-in disconnect detection
   */

  /**
   * Schedule token refresh before expiry
   */
  const scheduleTokenRefresh = useCallback(() => {
    if (refs.current.refreshTimer) {
      clearTimeout(refs.current.refreshTimer);
    }

    const cachedToken = refs.current.tokenManager.getCachedToken();
    if (!cachedToken) return;

    const now = Date.now();
    const timeUntilExpiry = cachedToken.expiresAt - now;
    const refreshTime = Math.max(timeUntilExpiry - 60000, 5000); // Refresh 60s before expiry, minimum 5s

    console.log(`⏰ Scheduling token refresh in ${Math.round(refreshTime / 1000)}s`);

    refs.current.refreshTimer = setTimeout(async () => {
      if (refs.current.socket?.connected) {
        try {
          console.log('🔄 Refreshing WebSocket token');
          const newToken = await refs.current.tokenManager.fetchToken();

          // Send reauth event to server
          refs.current.socket.emit('reauth', { token: newToken.token }, (ack: any) => {
            if (ack?.success) {
              console.log('✅ Token refresh successful');
              scheduleTokenRefresh(); // Schedule next refresh
            } else {
              console.error('❌ Token refresh failed:', ack);
              scheduleReconnect();
            }
          });
        } catch (error) {
          console.error('❌ Token refresh error:', error);
          scheduleReconnect();
        }
      }
    }, refreshTime);
  }, []);

  /**
   * Schedule reconnect with backoff + jitter
   */
  const scheduleReconnect = useCallback(() => {
    if (refs.current.reconnectTimer) {
      clearTimeout(refs.current.reconnectTimer);
    }

    // Clean up current connection
    if (refs.current.socket) {
      refs.current.socket.removeAllListeners();
      refs.current.socket.disconnect();
      refs.current.socket = null;
    }

    if (refs.current.heartbeatTimer) {
      clearInterval(refs.current.heartbeatTimer);
      refs.current.heartbeatTimer = null;
    }

    if (refs.current.refreshTimer) {
      clearTimeout(refs.current.refreshTimer);
      refs.current.refreshTimer = null;
    }

    refs.current.connecting = false;
    updateStatus('disconnected');

    // Don't reconnect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    const delay = calculateReconnectDelay(refs.current.reconnectAttempts);
    refs.current.reconnectAttempts++;

    console.log(`🔄 Scheduling reconnect attempt ${refs.current.reconnectAttempts} in ${Math.round(delay)}ms`);
    options.onReconnectScheduled?.(delay, refs.current.reconnectAttempts);

    refs.current.reconnectTimer = setTimeout(() => {
      connectWebSocket();
    }, delay);
  }, [isAuthenticated]);

  /**
   * Connect to WebSocket
   */
  const connectWebSocket = useCallback(async () => {
    // Single connection guard
    if (refs.current.connecting || refs.current.socket) {
      console.log('🛡️ Connection guard: already connecting or connected');
      return;
    }

    if (!isAuthenticated || !user) {
      console.log('🔒 Not authenticated, skipping WebSocket connection');
      updateStatus('disconnected');
      return;
    }

    refs.current.connecting = true;
    updateStatus('connecting');

    try {
      // Fetch token with singleflight (and optional override)
      const tokenData = await refs.current.tokenManager.fetchToken();

      // Create socket connection - convert HTTP/HTTPS to WS/WSS
      const wsUrl = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const socket = io(wsUrl, {
        auth: {
          token: tokenData.token,
        },
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 15000,
        reconnection: false, // We handle reconnection manually
      });

      refs.current.socket = socket;

      // Connection success
      socket.on('connect', () => {
        console.log('✅ WebSocket connected');
        refs.current.connecting = false;
        refs.current.reconnectAttempts = 0; // Reset attempts on success
        updateStatus('connected');

        // Join user room
        socket.emit('join', `user:${user.id}`);

        // Join execution room if we have an execution ID
        if (options.executionId) {
          joinExecutionRoom(options.executionId);
        }

        // Schedule token refresh (Socket.IO handles connection monitoring)
        scheduleTokenRefresh();
      });

      // Connection error
      socket.on('connect_error', (error) => {
        console.error('❌ WebSocket connection error:', error);
        refs.current.connecting = false;

        if (error.message?.includes('auth') || error.message?.includes('401')) {
          updateStatus('auth_failed');
          options.onAuthFailure?.(error.message || 'Authentication failed');
        } else {
          scheduleReconnect();
        }
      });

      // Disconnection
      socket.on('disconnect', (reason) => {
        console.log('🔌 WebSocket disconnected:', reason);
        refs.current.connecting = false;

        if (reason === 'io client disconnect') {
          // Intentional disconnect, don't reconnect
          updateStatus('disconnected');
        } else {
          // Network or server issue, attempt reconnect
          scheduleReconnect();
        }
      });

      // Pipeline events
      socket.on('pipeline_event', (event) => {
        handleDataUpdate({ events: [event] }, 'websocket');
        options.onEvent?.('pipeline_event', event);
      });

      socket.on('execution_update', (data) => {
        handleDataUpdate({ execution: data }, 'websocket');
        options.onEvent?.('execution_update', data);
      });

      socket.on('stage_update', (data) => {
        handleDataUpdate({ stage: data }, 'websocket');
        options.onEvent?.('stage_update', data);
      });

    } catch (error) {
      console.error('❌ WebSocket initialization failed:', error);
      refs.current.connecting = false;

      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Connection failed'
      }));

      if (error instanceof Error && error.message.includes('401')) {
        updateStatus('auth_failed');
        options.onAuthFailure?.(error.message);
      } else {
        scheduleReconnect();
      }
    }
  }, [isAuthenticated, user, updateStatus, options, handleDataUpdate, scheduleTokenRefresh, scheduleReconnect]);

  /**
   * Clean up all connections and timers
   */
  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up WebSocket connection');

    // Clear all timers
    if (refs.current.reconnectTimer) {
      clearTimeout(refs.current.reconnectTimer);
      refs.current.reconnectTimer = null;
    }

    if (refs.current.heartbeatTimer) {
      clearInterval(refs.current.heartbeatTimer);
      refs.current.heartbeatTimer = null;
    }

    if (refs.current.refreshTimer) {
      clearTimeout(refs.current.refreshTimer);
      refs.current.refreshTimer = null;
    }

    // Clean up socket
    if (refs.current.socket) {
      refs.current.socket.removeAllListeners();
      refs.current.socket.disconnect();
      refs.current.socket = null;
    }

    // Reset state
    refs.current.connecting = false;
    refs.current.reconnectAttempts = 0;
    refs.current.currentExecutionRoom = null;
    updateStatus('disconnected');
  }, [updateStatus]);

  /**
   * Manual reconnect
   */
  const reconnect = useCallback(() => {
    console.log('🔄 Manual reconnect requested');
    cleanup();
    if (isAuthenticated) {
      connectWebSocket();
    }
  }, [cleanup, isAuthenticated, connectWebSocket]);

  // Initialize on auth changes - ONLY depend on isAuthenticated
  useEffect(() => {
    if (isAuthenticated) {
      connectWebSocket();
    } else {
      cleanup();
    }

    return cleanup;
  }, [isAuthenticated]); // FIXED: Only depend on isAuthenticated

  // Handle execution room changes
  useEffect(() => {
    if (refs.current.socket?.connected && options.executionId) {
      joinExecutionRoom(options.executionId);
    }
  }, [options.executionId, joinExecutionRoom]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []); // FIXED: No dependencies for unmount cleanup

  /**
   * Force token refresh (demo/debugging)
   */
  const forceReauth = useCallback(async (): Promise<{ token: string; expiresAt: number }> => {
    console.log('🔄 Force reauth: clearing cache and fetching new token');

    try {
      const newToken = await refs.current.tokenManager.forceReauth();
      console.log('✅ Force reauth successful', {
        tokenLength: newToken.token.length,
        expiresIn: Math.round((newToken.expiresAt - Date.now()) / 1000)
      });
      return newToken;
    } catch (error) {
      console.error('❌ Force reauth failed:', error);
      throw error;
    }
  }, []);

  return {
    ...state,
    reconnect,
    disconnect: cleanup,
    forceReauth,
  };
}